import { netWorth } from "@grundbesitz/engine";
import type { PlayerId, PlayerState, PublicGameState } from "@grundbesitz/shared";
import { colorHex, formatMoney } from "../../lib/format";
import { downsample } from "./chartMath";

export interface RankedPlayer {
  rank: number;
  player: PlayerState;
  /** Net worth at the end (0 for players who dropped out). */
  worth: number;
  isWinner: boolean;
}

/**
 * Final ranking. The winner comes first, then the other remaining players by net worth, then the players
 * who dropped out, the ones who lasted longer first (judged by the last turn with any wealth).
 */
export function rankPlayers(game: PublicGameState): RankedPlayer[] {
  const lastAlive = (id: PlayerId): number => {
    let last = -1;
    for (const point of game.stats.wealthHistory) if ((point.worth[id] ?? 0) > 0) last = point.turn;
    return last;
  };
  const entries = game.players.map((player, order) => ({
    player,
    order,
    worth: netWorth(game, player.id),
    lastAlive: lastAlive(player.id),
  }));
  entries.sort((a, b) => {
    const aWin = a.player.id === game.winnerId;
    const bWin = b.player.id === game.winnerId;
    if (aWin !== bWin) return aWin ? -1 : 1;
    if (a.player.eliminated !== b.player.eliminated) return a.player.eliminated ? 1 : -1;
    if (!a.player.eliminated) return b.worth - a.worth || a.order - b.order;
    return b.lastAlive - a.lastAlive || a.order - b.order;
  });
  return entries.map((e, i) => ({ rank: i + 1, player: e.player, worth: e.worth, isWinner: e.player.id === game.winnerId }));
}

export function reasonText(reason: PublicGameState["gameOverReason"]): string | null {
  if (reason === "LAST_PLAYER") return "Letzter Spieler";
  if (reason === "TIME_LIMIT") return "Höchstes Vermögen nach Zeitlimit";
  return null;
}

// ---------------------------------------------------------------- dice

export interface DiceBar {
  sum: number;
  count: number;
  /** Expected number of rolls with this sum for the same number of fair rolls. */
  expected: number;
}

/** Number of ways two dice give `sum` (out of 36). */
export function waysToRoll(sum: number): number {
  return sum < 2 || sum > 12 ? 0 : 6 - Math.abs(7 - sum);
}

export function diceDistribution(diceSums: number[]): { bars: DiceBar[]; total: number } {
  const counts = Array.from({ length: 11 }, (_, i) => diceSums[i + 2] ?? 0);
  const total = counts.reduce((a, b) => a + b, 0);
  const bars = counts.map((count, i) => ({ sum: i + 2, count, expected: (total * waysToRoll(i + 2)) / 36 }));
  return { bars, total };
}

// ---------------------------------------------------------------- wealth over time

export interface WealthPoint {
  turn: number;
  worth: number;
}

export interface WealthSeries {
  id: PlayerId;
  name: string;
  token: PlayerState["token"];
  colorId: PlayerState["color"];
  color: string;
  eliminated: boolean;
  points: WealthPoint[];
}

export const MAX_CHART_POINTS = 160;

/**
 * One series per player from the recorded history. A player who dropped out is drawn up to the turn where
 * their wealth reached zero. The final state is appended so every line ends at the value shown in the ranking.
 */
export function buildWealthSeries(game: PublicGameState, maxPoints = MAX_CHART_POINTS): WealthSeries[] {
  const history = game.stats.wealthHistory;
  return game.players.map((player) => {
    const points: WealthPoint[] = [];
    let ended = false;
    for (const h of history) {
      const worth = h.worth[player.id] ?? 0;
      points.push({ turn: h.turn, worth });
      if (player.eliminated && worth === 0 && h.turn > 0) {
        ended = true;
        break;
      }
    }
    const lastTurn = points[points.length - 1]?.turn ?? -1;
    if (!ended && !player.eliminated && game.turnCounter > lastTurn) {
      points.push({ turn: game.turnCounter, worth: netWorth(game, player.id) });
    }
    return {
      id: player.id,
      name: player.name,
      token: player.token,
      colorId: player.color,
      color: colorHex(player.color),
      eliminated: player.eliminated,
      points: downsample(points, maxPoints),
    };
  });
}

/** Sorted unique turn numbers across all series, the x positions the crosshair snaps to. */
export function turnsOf(series: WealthSeries[]): number[] {
  const set = new Set<number>();
  for (const s of series) for (const p of s.points) set.add(p.turn);
  return [...set].sort((a, b) => a - b);
}

/** Worth of a series at `turn`: the last known point at or before it, or null when the line has ended earlier. */
export function worthAt(series: WealthSeries, turn: number): number | null {
  let value: number | null = null;
  for (const p of series.points) {
    if (p.turn > turn) break;
    value = p.worth;
  }
  const last = series.points[series.points.length - 1];
  if (series.eliminated && last && turn > last.turn) return null;
  return value;
}

export function describeWealth(series: WealthSeries[]): string {
  const maxTurn = Math.max(0, ...series.flatMap((s) => s.points.map((p) => p.turn)));
  const ends = series
    .map((s) => ({ s, last: s.points[s.points.length - 1] }))
    .filter((e): e is { s: WealthSeries; last: WealthPoint } => e.last !== undefined)
    .sort((a, b) => b.last.worth - a.last.worth)
    .map(({ s, last }) => (s.eliminated ? `${s.name} schied aus` : `${s.name} endet bei ${formatMoney(last.worth)}`));
  return `Liniendiagramm des Vermögens von Zug 0 bis Zug ${maxTurn}. ${ends.join(", ")}.`;
}

export function describeDice(bars: DiceBar[], total: number): string {
  if (total === 0) return "Balkendiagramm der Würfelsummen: noch keine Würfe.";
  const top = bars.reduce((best, b) => (b.count > best.count ? b : best), bars[0]!);
  return `Balkendiagramm der Würfelsummen 2 bis 12 bei ${total} Würfen. Am häufigsten: ${top.sum} mit ${top.count} Mal.`;
}
